import BookingCard from "./BookingCard";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import React, { useState, useEffect } from "react";
import { fetchCurrentBookings } from "services/dashboard.services";
import ReusableConfirmationModal from "modals/ReusableConfirmationModal";
import { approveBooking, cancelBooking } from "services/booking.services";
import { NoResultsSection, SkeletonBookingCard, MessageBox, FiltersDropdown } from "reusableComponents";
import { useModal } from "context";

import styles from "./styles.module.scss";

const BookingsListPage = () => {
  const { t } = useTranslation("global");
  const { openModal, closeModal } = useModal();
  const { token: accessToken } = useSelector((state) => state.auth);
  const [bookings, setBookings] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const [actionError, setActionError] = useState({});
  
  // Sort states
  const [sortBy, setSortBy] = useState("id"); // 'date' or 'id'
  const [sortOrder, setSortOrder] = useState("desc"); // 'asc' or 'desc'
  
  const navigate = useNavigate();

  const sortOptions = [
    { value: "date", label: t("dashboard.bookings.sortByDate") },
    { value: "id", label: t("dashboard.bookings.sortByReference") },
  ];

  const orderOptions = [
    { value: "desc", label: t("dashboard.bookings.sortDescending") },
    { value: "asc", label: t("dashboard.bookings.sortAscending") },
  ];

  const getBookings = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchCurrentBookings(accessToken, sortBy, sortOrder);
      setBookings(data.data);
    } catch (err) {
      const errorMessage = err.response?.data?.errors?.[0]?.message || 
                          err.message || 
                          t("dashboard.bookings.fetchError");
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getBookings();
  }, [accessToken, sortBy, sortOrder]); // Refetch when sort parameters change

  const handleSortByChange = (value) => {
    setSortBy(value);
  };

  const handleSortOrderChange = (value) => {
    setSortOrder(value);
  };

  const clearActionError = (bookingId) => {
    setActionError(prev => ({ ...prev, [bookingId]: null }));
  };

  const handleUserCancel = async (bookingId) => {
    setActionLoading(prev => ({ ...prev, [bookingId]: true }));
    setActionError(prev => ({ ...prev, [bookingId]: null }));
    
    try {
      await cancelBooking(bookingId, accessToken);
      openModal({
        type: "success",
        content: (
          <MessageBox
            type="success"
            message={t("dashboard.bookings.userCancelSuccess")}
            onClose={() => closeModal()}
          />
        ),
      });
      await getBookings(); // Refresh the list
      closeModal();
    } catch (error) {
      const errorMessage = error.message || t("errors.genericError");
      setActionError(prev => ({ ...prev, [bookingId]: errorMessage }));
      throw error;
    } finally {
      setActionLoading(prev => ({ ...prev, [bookingId]: false }));
    }
  };

  const showUserCancelModal = (bookingId) => {
    openModal({
      content: (
        <ReusableConfirmationModal
          message={t("dashboard.bookings.confirmUserCancelMessage")}
          onConfirm={() => handleUserCancel(bookingId)}
          onCancel={() => setActionError(prev => ({ ...prev, [bookingId]: null }))}
          confirmText={t("dashboard.bookings.confirmUserCancel")}
          cancelText={t("buttons.close")}
          loading={actionLoading[bookingId]}
          error={actionError[bookingId]}
        />
      ),
      title: t("dashboard.bookings.cancelBooking"),
    });
  };

  const handleReject = async (bookingId) => {
    setActionLoading(prev => ({ ...prev, [bookingId]: true }));
    clearActionError(bookingId);
    
    try {
      await cancelBooking(bookingId, accessToken);
      await getBookings(); // Refresh the list
      closeModal();
    } catch (error) {
      const errorMessage = error.response?.data?.errors?.[0]?.message || 
                          error.message || 
                          t("errors.genericError");
      setActionError(prev => ({ ...prev, [bookingId]: errorMessage }));
      throw new Error(errorMessage);
    } finally {
      setActionLoading(prev => ({ ...prev, [bookingId]: false }));
    }
  };

  const handleApprove = async (bookingId) => {
    setActionLoading(prev => ({ ...prev, [bookingId]: true }));
    clearActionError(bookingId);
    
    try {
      await approveBooking(bookingId, accessToken);
      await getBookings(); // Refresh the list
      closeModal();
    } catch (error) {
      const errorMessage = error.response?.data?.errors?.[0]?.message || 
                          error.message || 
                          t("errors.genericError");
      setActionError(prev => ({ ...prev, [bookingId]: errorMessage }));
      throw new Error(errorMessage);
    } finally {
      setActionLoading(prev => ({ ...prev, [bookingId]: false }));
    }
  };

  const showRejectModal = (bookingId) => {
    openModal({
      content: (
        <ReusableConfirmationModal
          message={t("dashboard.bookings.confirmCancelMessage")}
          onConfirm={() => handleReject(bookingId)}
          onCancel={() => clearActionError(bookingId)}
          confirmText={t("dashboard.bookings.confirmCancel")}
          loading={actionLoading[bookingId]}
          error={actionError[bookingId]}
        />
      ),
      title: t("dashboard.bookings.confirmCancelTitle"),
    });
  };

  const showApproveModal = (bookingId) => {
    openModal({
      content: (
        <ReusableConfirmationModal
          message={t("dashboard.bookings.confirmAcceptMessage")}
          onConfirm={() => handleApprove(bookingId)}
          onCancel={() => clearActionError(bookingId)}
          confirmText={t("dashboard.bookings.confirmAccept")}
          loading={actionLoading[bookingId]}
          error={actionError[bookingId]}
        />
      ),
      title: t("dashboard.bookings.confirmAcceptTitle"),
    });
  };

  if (error) {
    return (
      <div className={styles.error}>
        <MessageBox message={error} type="error" />
        <button onClick={getBookings} className={styles.retryButton}>
          {t("buttons.retry")}
        </button>
      </div>
    );
  }

  return (
    <div className={styles.bookingsListPage}>
      <div className={styles.header}>
        <h1 className={styles.title}>{t("dashboard.bookings.title")}</h1>
        
        <div className={styles.filtersContainer}>
          <FiltersDropdown
            label={t("dashboard.bookings.sortBy")}
            options={sortOptions}
            selected={sortBy}
            onSelect={handleSortByChange}
            placeholder={t("dashboard.bookings.selectSortBy")}
          />
          
          <FiltersDropdown
            label={t("dashboard.bookings.sortOrder")}
            options={orderOptions}
            selected={sortOrder}
            onSelect={handleSortOrderChange}
            placeholder={t("dashboard.bookings.selectSortOrder")}
          />
        </div>
      </div>

      {loading ? (
        <div className={styles.bookingsList}>
          {Array.from({ length: 5 }).map((_, index) => (
            <SkeletonBookingCard key={index} />
          ))}
        </div>
      ) : bookings.length === 0 ? (
        <NoResultsSection
          title={t("dashboard.bookings.noResultTitle")}
          text={t("dashboard.bookings.noResultText")}
          actionLabel={t("dashboard.bookings.browseSpaces")}
          onAction={() => navigate("/spaces")}
        />
      ) : (
        <ul className={styles.bookingsList}>
          {bookings.map((booking) => (
            <BookingCard
              key={booking.id}
              booking={booking}
              layout="currentBookings"
              onReject={() => showRejectModal(booking.id)}
              onApprove={() => showApproveModal(booking.id)}
              onUserCancel={() => showUserCancelModal(booking.id)}
              actionLoading={actionLoading[booking.id]}
              actionError={actionError[booking.id]}
              updateBookings={getBookings}
            />
          ))}
        </ul>
      )}
    </div>
  );
};

export default BookingsListPage;